// La portada del modo: la caja de prompt, las bases del catálogo y los diseños recientes
// (spec §3.1). Tocar una base o describir una prenda CREA un diseño y va a su lienzo.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ImagePlus, Loader2 } from 'lucide-react';
import CachedImg from '../../../../components/CachedImg';
import { useAgentEnv } from '../../agentEnvContext';
import { listDisenos, renombrarChat, type DisenoResumen } from '../../agentChatApi';
import { PromptBoxLibre } from '../promptLibre/PromptBoxLibre';
import { BASES_DE_INDUMENTARIA, CATEGORIAS_DE_BASES, type BaseDeIndumentaria, type CategoriaDeBase } from './basesDeIndumentaria';
import { ESTADO_BASE_DESCRITA, crearDiseno, idDeImagenPropia, rutaDelDiseno } from './disenosApi';
import { TITULO_DE_DISENO_MAX, tituloDeArchivo } from './disenoGuardado';
import { firmarPropias, subirBaseDelCatalogo, subirImagenDeDiseno } from './generarEnDiseno';
import { TituloDeDiseno } from './TituloDeDiseno';

const TODAS = 'Todas';

/** Los chips de Plantillas, tal cual (`InterfazV2Plantillas`). */
const CHIP = 'shrink-0 px-4 py-2 rounded-full text-[14px] font-medium transition-colors border';

export const PortadaDeDiseno: React.FC = () => {
  const navigate = useNavigate();
  const { companyId, userId } = useAgentEnv();
  const [prompt, setPrompt] = useState('');
  const [categoria, setCategoria] = useState<CategoriaDeBase | typeof TODAS>(TODAS);
  const [creando, setCreando] = useState<string | null>(null);
  const location = useLocation();
  const [error, setError] = useState<string | null>(
    () => ((location.state as { aviso?: unknown } | null)?.aviso as string | undefined) ?? null,
  );
  const [recientes, setRecientes] = useState<DisenoResumen[]>([]);
  const [portadas, setPortadas] = useState<Map<string, string>>(new Map());

  // `desdeBase` y `desdeDescripcion` cruzan un `await` antes de tocar estado y navegar:
  // si el componente se desmontó en el medio (se fue a otro diseño reciente, que no
  // queda deshabilitado por `creando`), la resolución tardía no tiene que setear estado
  // ni navegar de nuevo. Mismo patrón que el `vivo` del efecto de abajo, pero en un ref
  // porque estos dos lo leen desde un callback, no desde un efecto.
  const vivoRef = useRef(true);
  useEffect(() => {
    vivoRef.current = true;
    return () => {
      vivoRef.current = false;
    };
  }, []);

  // El aviso ya se leyó al estado inicial: se limpia del history para que volver atrás
  // (o recargar) no lo vuelva a mostrar. `replace` con `state: null`, como el efecto de
  // la base descrita del lienzo.
  useEffect(() => {
    if ((location.state as { aviso?: unknown } | null)?.aviso === undefined) return;
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let vivo = true;
    void listDisenos().then(async (lista) => {
      if (!vivo) return;
      setRecientes(lista);
      const firmas = await firmarPropias(lista.flatMap((d) => (d.portada ? [d.portada] : [])));
      if (vivo) setPortadas(firmas);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const renombrar = useCallback(async (id: string, titulo: string) => {
    const ok = await renombrarChat(id, titulo);
    if (ok) setRecientes((r) => r.map((d) => (d.id === id ? { ...d, titulo } : d)));
    else setError('No se pudo cambiar el nombre. Probá de nuevo.');
    return ok;
  }, []);

  const bases = useMemo(
    () => (categoria === TODAS ? BASES_DE_INDUMENTARIA : BASES_DE_INDUMENTARIA.filter((b) => b.categoria === categoria)),
    [categoria],
  );

  const desdeBase = useCallback(
    async (b: BaseDeIndumentaria) => {
      if (creando || !companyId || !userId) return;
      setCreando(b.id);
      setError(null);
      try {
        const subida = await subirBaseDelCatalogo(b.archivo, b.id, companyId);
        if (!vivoRef.current) return;
        const id = await crearDiseno({
          userId,
          companyId,
          titulo: b.nombre,
          imagenes: [{ id: idDeImagenPropia(), titulo: b.nombre, ...subida, creadaEn: Date.now() }],
        });
        if (!vivoRef.current) return;
        if (!id) throw new Error('No se pudo crear el diseño.');
        navigate(rutaDelDiseno(id));
      } catch (e) {
        if (!vivoRef.current) return;
        setError(e instanceof Error ? e.message : 'No se pudo crear el diseño.');
        setCreando(null);
      }
    },
    [creando, companyId, userId, navigate],
  );

  // Una foto propia como base (la card "Comenzar desde una imagen", pedido de Matías del
  // 2026-09-18): se sube como cualquier imagen del diseño y el diseño nace con ella.
  const desdeArchivo = useCallback(
    async (archivo: File) => {
      if (creando || !companyId || !userId) return;
      setCreando('archivo');
      setError(null);
      try {
        const subida = await subirImagenDeDiseno(archivo, companyId);
        if (!vivoRef.current) return;
        const titulo = tituloDeArchivo(archivo.name);
        const id = await crearDiseno({
          userId,
          companyId,
          titulo,
          imagenes: [{ id: idDeImagenPropia(), titulo, ...subida, creadaEn: Date.now() }],
        });
        if (!vivoRef.current) return;
        if (!id) throw new Error('No se pudo crear el diseño.');
        navigate(rutaDelDiseno(id));
      } catch (e) {
        if (!vivoRef.current) return;
        setError(e instanceof Error ? e.message : 'No se pudo crear el diseño.');
        setCreando(null);
      }
    },
    [creando, companyId, userId, navigate],
  );

  const desdeDescripcion = useCallback(async () => {
    const texto = prompt.trim();
    if (!texto || creando || !companyId || !userId) return;
    setCreando('descripcion');
    setError(null);
    try {
      const id = await crearDiseno({ userId, companyId, titulo: texto.slice(0, TITULO_DE_DISENO_MAX), imagenes: [] });
      if (!vivoRef.current) return;
      if (!id) throw new Error('No se pudo crear el diseño.');
      navigate(rutaDelDiseno(id), { state: { [ESTADO_BASE_DESCRITA]: texto } });
    } catch (e) {
      if (!vivoRef.current) return;
      setError(e instanceof Error ? e.message : 'No se pudo crear el diseño.');
      setCreando(null);
    }
  }, [prompt, creando, companyId, userId, navigate]);

  return (
    <div className="flex h-[calc(100dvh-env(safe-area-inset-top)-4.25rem)] flex-col overflow-hidden md:h-[100dvh]">
      <header className="flex h-16 shrink-0 items-center px-6">
        <h1 className="text-[15px] font-semibold text-brand-black">Diseño de indumentaria</h1>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="mt-4 text-[32px] font-[700] leading-[1.2] tracking-[-0.02em] text-brand-black">
            ¿Qué prenda diseñamos?
          </h2>
          <p className="mt-2 text-[15px] text-[#3C3C43]/60">Describila, o partí de un plano técnico de abajo.</p>
          <div className="mt-6">
            <PromptBoxLibre
              prompt={prompt}
              onPromptChange={setPrompt}
              onSubmit={() => void desdeDescripcion()}
              adjuntos={[]}
              onQuitarAdjunto={() => {}}
              conEnviar
              enviarDeshabilitado={!prompt.trim() || creando !== null}
              disabled={creando !== null}
              placeholder="Describí la prenda (ej.: remera oversize con capucha y bolsillo canguro)"
            />
          </div>
          {error && <p className="mt-3 text-[13px] text-red-600">{error}</p>}

          {recientes.length > 0 && (
            <section className="mt-10">
              <h3 className="text-[13px] font-normal uppercase tracking-wide text-[#3C3C43]/60">Diseños recientes</h3>
              <div className="mt-3 flex gap-3 overflow-x-auto pb-2">
                {recientes.map((d) => {
                  const src = d.portada ? portadas.get(`${d.portada.bucket}/${d.portada.path}`) : undefined;
                  // La card no es un botón entero: la foto abre el diseño y el nombre se
                  // edita en el lugar (`TituloDeDiseno`), que es otro botón.
                  return (
                    <div
                      key={d.id}
                      className="group w-40 shrink-0 rounded-2xl border border-black/[0.06] bg-white p-2 transition-colors hover:border-[#1644EB]/[0.35]"
                    >
                      <button
                        type="button"
                        onClick={() => navigate(rutaDelDiseno(d.id))}
                        disabled={creando !== null}
                        aria-label={`Abrir ${d.titulo}`}
                        className="block aspect-square w-full overflow-hidden rounded-xl bg-[#F3F4F8] transition-opacity hover:opacity-90 disabled:opacity-60"
                      >
                        {src && <CachedImg src={src} alt="" className="h-full w-full object-cover" />}
                      </button>
                      <div className="mt-2 px-1">
                        <TituloDeDiseno variante="card" titulo={d.titulo} onRenombrar={(t) => renombrar(d.id, t)} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="mt-10">
            <div className="flex gap-2 overflow-x-auto py-1">
              {[TODAS, ...CATEGORIAS_DE_BASES].map((c) => {
                const activa = c === categoria;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategoria(c as CategoriaDeBase | typeof TODAS)}
                    aria-pressed={activa}
                    className={`${CHIP} ${activa ? 'border-brand-primary text-brand-primary bg-[#EBEFFE]' : 'border-black/[0.06] bg-white hover:bg-black/[0.03]'}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4 md:grid-cols-4 lg:grid-cols-5">
              {/* La primera card no es una base: es subir una foto propia. Un <label> con
                  el input adentro, así toda la card abre el selector. */}
              <label
                className={`flex cursor-pointer flex-col rounded-2xl border border-black/[0.06] bg-white p-2 text-left transition-colors hover:border-[#1644EB]/[0.35] ${
                  creando !== null ? 'pointer-events-none opacity-60' : ''
                }`}
              >
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  disabled={creando !== null}
                  onChange={(e) => {
                    const archivo = e.target.files?.[0];
                    e.target.value = '';
                    if (archivo) void desdeArchivo(archivo);
                  }}
                />
                <span className="flex aspect-square w-full items-center justify-center rounded-xl bg-white">
                  {creando === 'archivo' ? (
                    <Loader2 className="h-6 w-6 animate-spin text-brand-primary" aria-hidden="true" />
                  ) : (
                    <ImagePlus className="h-7 w-7 text-[#02062B]/[0.55]" strokeWidth={1.75} aria-hidden="true" />
                  )}
                </span>
                <span className="mt-2 px-1 text-[13px] font-semibold leading-snug text-brand-black">Comenzar desde una imagen</span>
              </label>
              {bases.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => void desdeBase(b)}
                  disabled={creando !== null}
                  className="rounded-2xl border border-black/[0.06] bg-white p-2 text-left transition-colors hover:border-[#1644EB]/[0.35] disabled:opacity-60"
                >
                  <div className="relative aspect-square overflow-hidden rounded-xl bg-white">
                    <img src={b.archivo} alt={b.nombre} className="h-full w-full object-contain" loading="lazy" />
                    {creando === b.id && (
                      <span className="absolute inset-0 flex items-center justify-center bg-white/70">
                        <Loader2 className="h-5 w-5 animate-spin text-brand-primary" aria-hidden="true" />
                      </span>
                    )}
                  </div>
                  <p className="mt-2 truncate px-1 text-[13px] font-semibold text-brand-black">{b.nombre}</p>
                  <p className="truncate px-1 text-[12px] text-[#3C3C43]/55">{b.categoria}</p>
                </button>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
